import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

export const updateSearchCounts = createAsyncThunk(
	"automatedSearchPanelView/updateSearchCounts",
	async ({searchId, workspaceId}, {getState}) => {
		const state = getState()
		const sessionStartTimestamp = state.sessionTimestamp.value
		const {data} = await axios.get(`/api/workspace/${workspaceId}/automatedSearch/${searchId}/count`, {
			params: { sessionStartTimestamp },
		})
		return {searchId, ...data, workspaceId}
	}
)

export const getAutomatedSearchList = createAsyncThunk(
	"automatedSearchPanelView/getSearches",
	async ({workspaceId}, {getState}) => {
		const state = getState()
		const sessionStartTimestamp = state.sessionTimestamp.value
		const { data } = await axios.get(`/api/workspace/${workspaceId}/automatedSearch/`, {
			params: { sessionStartTimestamp },
		})
		return {...data, workspaceId}
	}
)

export const AUTOMATED_SEARCH_PANEL_VIEW = {
	ABOUT: "ABOUT",
	AUTOMATED_SEARCH_FORM: "AUTOMATED_SEARCH_FORM",
	SEARCH_LIST: "SEARCH_LIST",
	ARTICLE_LIST: "ARTICLE_LIST",
}

const DEFAULT_PAGE_OBJECT = { page: 1, fetchInitialPage: false }
const initialState = {
	value: AUTOMATED_SEARCH_PANEL_VIEW.SEARCH_LIST,
	selectedSearch: null,
	searchListPosition: 0,
	automatedSearchList: [],
	countMap: {},
	isListLoading: false,
	importedExcludedList: [],
	offsetMap: {},
	/**
	 * a map of page objects keyed by autoSearch.id.
	 *  @type DEFAULT_PAGE_OBJECT
	 */
	pageMap: {},
	searchResultListPositionMap: {}
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const automatedSearchPanelViewSlice = createSlice({
	name: "automatedSearchPanelView",
	initialState: {
		byWorkspace: {},
	},
	reducers: {
		setAutomatedSearchPanelView: (state, action) => {
			const {view, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.value = view
		},
		setSelectedSearch: (state, action) => {
			const {search, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.selectedSearch = search
		},
		setAutoSearchListPosition: (state, action) => {
			const {ratio, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.searchListPosition = ratio
		},
		setPageMap: (state, action) => {
			//TODO: not used?
			const {selectedSearch, page, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.pageMap[selectedSearch.id] = {page}
		},
		incrementPageCount: (state, action) => {
			const {selectedSearch, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const pageObject = workspace.pageMap[selectedSearch.id]
			if (pageObject?.page) {
				workspace.pageMap[selectedSearch.id] = { page: pageObject.page + 1}
			} else {
				workspace.pageMap[selectedSearch.id] = { page: 1 }
			}
		},
		initializePageCount: (state, action) => {
			const {selectedSearch, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const pageObject = workspace.pageMap[selectedSearch.id]
			if (pageObject?.page) {
				workspace.pageMap[selectedSearch.id] = {page: pageObject.page, fetchInitialPage: true}
			} else {
				workspace.pageMap[selectedSearch.id] = {page: 1, fetchInitialPage: false}
			}
		},
		setFetchInitialPage: (state, action) => {
			const {selectedSearch, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const pageObject = workspace.pageMap[selectedSearch.id]
			if (pageObject) {
				pageObject.fetchInitialPage = true
			}
			
			workspace.pageMap[selectedSearch.id] = pageObject
		},
		setAutoSearchResultListPosition: (state, action) => {
			const {searchId, position, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.searchResultListPositionMap[searchId] = position
		},
		pushImportedExcludedList: (state, action) => {
			const {pmid, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			// Check if the id is already in the list
			if (!workspace.importedExcludedList.includes(pmid)) {
				// If the id is not in the list, push it
				workspace.importedExcludedList.push(pmid)
			}
			workspace.offsetMap[workspace.selectedSearch?.id] = workspace.importedExcludedList.length
		},
		removeArticleFromImportedExcludedList: (state, action) => {
			const {pmid, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			// Remove the id from the list
			workspace.importedExcludedList = workspace.importedExcludedList.filter(item => item !== pmid)
		},
		resetImportedExcludedList: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.importedExcludedList = initialState.importedExcludedList
		},
		upsertSingleSearch: (state, action) => {
			const {search, counts, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const index = workspace.automatedSearchList.findIndex(oldSearch => oldSearch.id === search.id)
		
			if (index !== -1) {
				// Update existing search
				workspace.automatedSearchList[index] = search
			} else {
				// Insert new search
				workspace.automatedSearchList.push(search)
			}
			workspace.countMap = {
				...workspace.countMap,
				...counts
			}
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				updateSearchCounts.fulfilled,
				(state, action) => {
					const { counts, workspaceId } = action.payload
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.countMap = {
						...workspace.countMap,
						...counts
					}
				}
			)
			.addCase(
				updateSearchCounts.pending,
				(state, action) => {
					// set loading state for search id
					const {searchId, workspaceId} = action.meta.arg
					const workspace = ensureWorkspace(state, workspaceId)
					if (workspace.countMap[searchId]) {
						workspace.countMap[searchId].isLoading = true
					}
				}
			)
			.addCase(
				getAutomatedSearchList.pending,
				(state, action) => {
					const {workspaceId} = action.meta.arg
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.isListLoading = true
				}
			)
			.addCase(
				getAutomatedSearchList.fulfilled,
				(state, action) => {
					const {searches, counts, workspaceId} = action.payload
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.automatedSearchList = searches
					workspace.countMap = counts
					workspace.isListLoading = false
				}
			)
	}
})

export const { setFetchInitialPage, initializePageCount, incrementPageCount, setAutomatedSearchPanelView, setPageMap, setSelectedSearch, setAutoSearchListPosition, setAutoSearchResultListPosition, upsertSingleSearch, pushImportedExcludedList, resetImportedExcludedList, removeArticleFromImportedExcludedList } =
	automatedSearchPanelViewSlice.actions

export const selectAutoSearchResultListScrollPositionMap = (workspaceId) => createSelector(
	(state) => state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.searchResultListPositionMap ?? initialState.searchResultListPositionMap,
	(state, searchId) => searchId,
	(searchResultListPositionMap, searchId) => searchResultListPositionMap[searchId] ?? 0
)

export const selectAutoSearchPage = (workspaceId) => createSelector(
	(state) => state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.pageMap ?? initialState.pageMap,
	(state, searchId) => searchId,
	(pageMap, searchId) => pageMap[searchId] ?? DEFAULT_PAGE_OBJECT
)

export const selectAutomatedSearchPanelView = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.value ?? initialState.value

export const selectAutomatedSearchSelectedSearch = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.selectedSearch ?? initialState.selectedSearch

export const selectAutoSearchListScrollPosition = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.searchListPosition ?? initialState.searchListPosition

export const selectSearchCounts = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.countMap ?? initialState.countMap

export const selectSearches = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.automatedSearchList ?? initialState.automatedSearchList

export const selectIsLoading = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.isListLoading ?? initialState.isListLoading

export const selectImportedExcludedCount = (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.importedExcludedList.length ?? 0

export const selectAutoSearchResultPageMap= (state, workspaceId) =>
	state.automatedSearchPanelView.byWorkspace?.[workspaceId]?.pageMap ?? initialState.pageMap


export default automatedSearchPanelViewSlice.reducer
